import ReactJson from '@microlink/react-json-view'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DetailsIcon from '@mui/icons-material/Details';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Link, Slide, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box, useTheme } from '@mui/system';
import { DataGrid, GridFooter } from '@mui/x-data-grid';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { DataAPIContext, GET_ONE } from '../api';
import { copyTextToClipboard, useIsWidthUp } from '../utils';
import RecordList from './RecordList';

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(2),
        marginBottom: theme.spacing(0),
        display: 'flex',
        height: `calc(${theme.cp.contentHeight} - 9rem)`,
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 11rem)`,
            margin: 0,
        },
    },
    rootNarrow: {
        margin: 0,
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
    },
    recordList: {
        borderRadius: '0 0 15px 15px !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
        },
    },
    recordListNarrow: {
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const GridFooterToolbar = (props) => {
    const isXLargeScreen = useIsWidthUp('xl')
    return (
        <Grid container direction="row" alignItems="center" spacing={2} sx={{ ml: 0 }}>
            <Grid item xs={6} container >
                {props.revSelection && props.revSelection.length === 2 ? <>
                    <Button variant="contained" size="small" color="primary" startIcon={<CompareArrowsIcon />} onClick={props.onCompareRevisions}>
                        {isXLargeScreen && "Compare two revisions"}
                    </Button>
                </> : <Typography variant="body1"> Select 2 {isXLargeScreen && "revisions to compare them"}  </Typography>}
            </Grid>
            <Grid item xs={6} container justifyContent="flex-end">
                <GridFooter sx={{ border: 'none', paddingRight: "1rem" }} />
            </Grid>
        </Grid>
    )
}

export const RevisionHistoryDialog = (props) => {
    const theme = useTheme();
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();
    const isNarrow = props.layout === 'narrow';
    const [rev, setRev] = React.useState(null);
    const [revId, setRevId] = React.useState(null);
    const [version, setVersion] = React.useState(null);
    const [revSelection, setRevSelection] = React.useState([]);
    const [revCompare, setRevCompare] = React.useState(null);
    const [compareData, setCompareData] = React.useState(null);
    const [gridRows, setGridRows] = React.useState(null);
    const [diff, setDiff] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const rowMap = {};
    !!gridRows && gridRows.forEach(row => {
        rowMap[row.id] = row;
    });

    const compareDataRows = [];
    if (compareData) {
        compareData.added?.forEach(x => compareDataRows.push({ ...x, action: "Added", id: compareDataRows.length }));
        compareData.changed?.forEach(x => compareDataRows.push({
            ...x.version,
            diff: (x.diff || []).map((z, zidx) => ({ ...z, id: zidx })),
            action: "Updated",
            id: compareDataRows.length
        }));
        compareData.removed?.forEach(x => compareDataRows.push({ ...x, action: "Removed", id: compareDataRows.length }));
    }

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const onRowClick = (params, ev, args) => {
        console.log('rev row change', params, args);
        setRevId(params.row.revision.id);
    }

    React.useEffect(() => {
        if (!revId || (rev && +rev.id === +revId)) { return; }
        let active = true;
        dataAPI(GET_ONE, props.resource, { id: props.id, urlSuffix: `get_revision/?revid=${revId}` })
            .then(res => {
                if (!active) { return; }
                console.log('rev details', res);
                setRev(res.data);
            })
        return () => { active = false; }
    });

    React.useEffect(() => {
        if (!revCompare || !!compareData) { return; }
        let active = true;
        dataAPI(GET_ONE, props.resource, { id: props.id, urlSuffix: `compare_revisions/?rev1=${revCompare[0]}&rev2=${revCompare[1]}` })
            .then(res => {
                if (!active) { return; }
                console.log('comparison details', res);
                setCompareData(res.data);
            })
        return () => { active = false; }
    })

    const onDetailsClose = (ev) => {
        setRev(null);
        setRevId(null);
    }

    const onCompareClose = (ev) => {
        setRevCompare(null);
        setCompareData(null);
    }

    const onSelectionModelChange = (ev, selmodel) => {
        console.log('selmodel', selmodel);
        setRevSelection(selmodel);
    }

    const onRowsChanged = (rows) => {
        setGridRows(rows);
    }

    const onCompareRevisions = (ev) => {
        // convert row IDs to revision IDs and sort them to make sure that the earliest revision is always first
        const revIds = revSelection.map(x => rowMap[x.toString()]?.revision.id).toSorted();
        setRevCompare(revIds);
    }

    const versionComparator = (a, b) => {
        if (a.content_type.name !== b.content_type.name) {
            return a.content_type.name.localeCompare(b.content_type.name);
        } else {
            return +a.object_id - +b.object_id;
        }
    }

    const compareDataRowsComparator = (a, b) => {
        if (a.action !== b.action) {
            return a.action.localeCompare(b.action);
        } else if (a.content_type.name !== b.content_type.name) {
            return a.content_type.name.localeCompare(b.content_type.name);
        } else {
            return +a.object_id - +b.object_id;
        }
    }

    const requestIdCell = (p) => {
        if (!p.row.revision.meta?.request_id) {
            return <div>&mdash;</div>;
        }
        const requestId = p.row.revision.meta.request_id;
        if (isNarrow) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: 0.5 }}>
                    <Typography variant="body2" noWrap title={requestId} sx={{ flex: 1, minWidth: 0 }}>
                        {requestId}
                    </Typography>
                    <IconButton
                        aria-label="copy request id to clipboard"
                        size="small"
                        onClick={(ev) => {
                            copyToClipboard(requestId, 'Request ID');
                            ev.preventDefault();
                            ev.stopPropagation();
                        }}
                    >
                        <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }
        return (
            <Box alignItems="center">
                <TextField
                    variant="standard"
                    value={requestId}
                    fullWidth
                    title={requestId}
                    size="small"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <IconButton
                                aria-label="copy activity id to clipboard"
                                onClick={(ev) => {
                                    copyToClipboard(requestId, 'Request ID');
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    return false;
                                }}
                                edge="end"
                            >
                                <FileCopyOutlinedIcon />
                            </IconButton>
                        ),
                    }}
                />
            </Box>
        );
    };

    const revisionColumns = isNarrow
        ? [
              { field: 'id', headerName: 'Revision', sortable: false, width: 88, valueGetter: (p) => p.row.revision.id },
              {
                  field: 'date_created',
                  headerName: 'Timestamp',
                  sortable: false,
                  flex: 1.2,
                  minWidth: 150,
                  valueGetter: (p) => moment(p.row.revision.date_created),
                  valueFormatter: (p) => p.value.format('MMM DD, YYYY h:mm:ss a z'),
              },
              {
                  field: 'user',
                  headerName: 'User',
                  sortable: false,
                  flex: 0.9,
                  minWidth: 80,
                  renderCell: (p) => {
                      if (!p.row.revision.user) {
                          return <div>&mdash;</div>;
                      }
                      return (
                          <Link href={`/user/${p.row.revision.user.id}`} target="_blank">
                              {p.row.revision.user.display_name}
                          </Link>
                      );
                  },
              },
              { field: 'comment', headerName: 'Description', sortable: false, flex: 1.5, minWidth: 100, valueGetter: (p) => p.row.revision.comment },
              { field: 'request_id', headerName: 'Request ID', sortable: false, flex: 1, minWidth: 110, renderCell: requestIdCell },
          ]
        : [
              { field: 'id', headerName: 'Revision', sortable: false, flex: 0.5, valueGetter: (p) => p.row.revision.id },
              {
                  field: 'date_created',
                  headerName: 'Timestamp',
                  sortable: false,
                  flex: 1,
                  valueGetter: (p) => moment(p.row.revision.date_created),
                  valueFormatter: (p) => p.value.format('MMM DD, YYYY h:mm:ss a z'),
              },
              {
                  field: 'user',
                  headerName: 'User',
                  sortable: false,
                  flex: 1,
                  renderCell: (p) => {
                      if (!p.row.revision.user) {
                          return <div>&mdash;</div>;
                      }
                      return (
                          <Link href={`/user/${p.row.revision.user.id}`} target="_blank">
                              {p.row.revision.user.display_name}
                          </Link>
                      );
                  },
              },
              { field: 'comment', headerName: 'Description', sortable: false, flex: 2, valueGetter: (p) => p.row.revision.comment },
              { field: 'request_id', headerName: 'Request ID', sortable: false, flex: 1, renderCell: requestIdCell },
          ];

    return (
        <div className={isNarrow ? classes.rootNarrow : classes.root} style={props.style}>
        {rev && <>
            <Dialog fullWidth maxWidth="xl" open={true} onClose={onDetailsClose} TransitionComponent={Transition}>
                <DialogTitle>{rev.comment}</DialogTitle>
                <DialogContent>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={6} sm={3}><Typography variant="body2">Rev. {rev.id}</Typography></Grid>
                        <Grid item xs={6} sm={3}><Typography variant="body2">{moment(rev.date_created).format("ddd, MMM Do YYYY, h:mm:ss a z")}</Typography></Grid>
                        <Grid item xs={12} sm={3}>
                            {rev.user && <>
                                <Typography variant="body2"><Link href={`/user/${rev.user.id}`} target="_blank">{rev.user.display_name}</Link></Typography>
                            </>}
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            {rev.meta?.request_id &&
                                <Typography variant="body2">
                                    {rev.meta?.request_id} <IconButton onClick={ev => { copyToClipboard("abcdef", 'Request ID'); }}><FileCopyOutlinedIcon /></IconButton>
                                </Typography>
                            }
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        {version ? <>
                            <Box sx={{ mb: theme.spacing(1), mt: theme.spacing(1) }}>
                                <Button color="primary" variant="text" onClick={ev => setVersion(null)} startIcon={<ArrowBackIcon />}>Back to version list</Button>
                            </Box>
                            <ReactJson src={version.serialized_data} />
                        </> : <>
                            <Box sx={{ height: 400, width: '100%', mt: theme.spacing(2) }}>
                                <DataGrid
                                    autoHeight={false}
                                    checkboxSelection={false}
                                    density="compact"
                                    pagination={false}
                                    rows={(rev.versions || []).toSorted(versionComparator)}
                                    columns={[
                                        { field: "type", headerName: "Type", flex: 1, valueGetter: p => p.row.content_type.name },
                                        { field: "object_id", headerName: "Object ID", flex: 1, valueGetter: p => p.row.object_id },
                                        {
                                            field: "repr", headerName: "Label", flex: 2, renderCell: p => (
                                                p.row.link ? <Link href={p.row.link} target="_blank">{p.row.object_repr}</Link> : p.row.object_repr)
                                        },
                                        {
                                            field: "actions", headerName: "Actions", flex: 0.3, renderCell: p => (
                                                <Box>
                                                    <IconButton title="Details" onClick={ev => setVersion(p.row)}><DetailsIcon fontSize="small" /></IconButton>
                                                </Box>
                                            )
                                        }
                                    ]}
                                />
                            </Box>
                        </>}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" variant="contained" onClick={onDetailsClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </>}

        {revCompare && <>
            <Dialog fullWidth maxWidth="xl" open={true} sx={{ zIndex: 100000 }} onClose={onCompareClose} TransitionComponent={Transition}>
                <DialogTitle>Compare revisions {revCompare[0]} and {revCompare[1]}</DialogTitle>
                <DialogContent>
                    {!compareData && <LinearProgress />}
                    {!!compareData && <>
                        <Grid container direction="row" spacing={2}>
                            {!compareDataRows.length && <Grid item xs={12}><Typography>No changes found</Typography></Grid>}
                            {compareDataRows.length && <Grid item xs={12}>
                                {diff && <>
                                    <Box sx={{ mb: theme.spacing(1), mt: theme.spacing(1) }}>
                                        <Button color="primary" variant="text" onClick={ev => setDiff(null)} startIcon={<ArrowBackIcon />}>Back to comparison</Button>
                                    </Box>
                                    {/* <ReactJson src={diff.diff}/> */}
                                    <Grid container direction="row" spacing={2}>
                                        <Grid item xs={4}><Typography fontWeight={500}>Field</Typography></Grid>
                                        <Grid item xs={8}><Typography fontWeight={500}>Change</Typography></Grid>
                                        {diff.diff.map(row => <>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">{row.field}</Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <div dangerouslySetInnerHTML={{ __html: row.html }} />
                                            </Grid>
                                        </>)}
                                    </Grid>
                                </>}
                                {version && <>
                                    <Box sx={{ mb: theme.spacing(1), mt: theme.spacing(1) }}>
                                        <Button color="primary" variant="text" onClick={ev => setVersion(null)} startIcon={<ArrowBackIcon />}>Back to comparison</Button>
                                    </Box>
                                    <ReactJson src={version.serialized_data} />
                                </>}
                                {!diff && !version && <>
                                    <Box sx={{ height: 400, width: '100%', mt: theme.spacing(2) }}>
                                        <DataGrid
                                            autoHeight={false}
                                            density="compact"
                                            pagination={false}
                                            rows={compareDataRows.toSorted(compareDataRowsComparator)}
                                            columns={[
                                                { field: "action", headerName: "Action", flex: 1, valueGetter: p => p.row.action },
                                                { field: "type", headerName: "Type", flex: 1, valueGetter: p => p.row.content_type.name },
                                                { field: "object_id", headerName: "Object ID", flex: 1, valueGetter: p => p.row.object_id },
                                                {
                                                    field: "repr", headerName: "Label", flex: 2, renderCell: p => (
                                                        p.row.link ? <Link href={p.row.link} target="_blank">{p.row.object_repr}</Link> : p.row.object_repr)
                                                },
                                                {
                                                    field: "actions", headerName: "Actions", flex: 0.3, renderCell: p => (
                                                        <Box>
                                                            {p.row.action === "Updated" && <IconButton title="Show diff" onClick={ev => setDiff(p.row)}><DetailsIcon fontSize="small" /></IconButton>}
                                                            {(p.row.action === "Added" || p.row.action === "Removed") && <IconButton title="Show details" onClick={ev => setVersion(p.row)}><DetailsIcon fontSize="small" /></IconButton>}
                                                        </Box>
                                                    )
                                                }
                                            ]}
                                        />
                                    </Box>
                                </>}
                            </Grid>}
                        </Grid>
                    </>}
                </DialogContent>
                <DialogActions>
                    <Button color="primary" variant="contained" onClick={onCompareClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </>}

        <RecordList
            className={isNarrow ? `${classes.recordList} ${classes.recordListNarrow}` : classes.recordList}
            resource={props.resource}
            urlSuffix={`${props.id}/list_revisions/`}
            onRowClick={onRowClick}
            checkboxSelection={true}
            selectionModel={revSelection}
            onSelectionModelChange={onSelectionModelChange}
            onRowsChanged={onRowsChanged}
            isSmallScreen={props.isSmallScreen}
            disableListBorder={false}
            limitHeightForMobile={'40vh'}
            disableColumnMenu={true}
            slots={{
                Footer: GridFooterToolbar,
            }}
            slotProps={{
                footer: { revSelection, onCompareRevisions },
            }}
            columns={revisionColumns}
        />
    </div>
    );
};

export default RevisionHistoryDialog;
